import { parseISOToLocalDate } from '../../utils/dateUtils';

/** The two date fields every signature-shaped response carries. */
export interface SignatureDates {
  /** When the ceremony actually happened, as an ISO instant. */
  signed_at: string;
  /** The calendar date the signer attested to, when they declared one. */
  signature_date?: string | null;
}

const DATE_ONLY: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
};

const TIME_OF_DAY: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

/**
 * When the signing ceremony actually happened, to the second, in UTC.
 *
 * The forensic answer, for the audit trail. Clinical surfaces want
 * {@link formatSignatureDate} instead.
 */
export function formatCeremonyTimestamp(signedAt: string): string {
  const dt = new Date(signedAt);
  if (Number.isNaN(dt.getTime())) {
    return '';
  }
  // Fixed UTC so every viewer sees the same instant; en-US for readability.
  const formatted = dt.toLocaleString('en-US', {
    timeZone: 'UTC',
    ...DATE_ONLY,
    ...TIME_OF_DAY,
    second: '2-digit',
  });
  return `${formatted} UTC`;
}

/**
 * How a signature's date reads on a clinical surface.
 *
 * A clinician may sign a visit days after performing it and declare the date
 * they are attesting to, so that declared date — not the server clock — is what
 * the chart, the printed note and this app show. Signatures taken before the
 * field existed, and ceremonies that collect no date (QA approvals, bypasses),
 * have none, and fall back to the instant they were signed.
 *
 * That fallback renders in the *viewer's* timezone, which is what these
 * surfaces showed before a declared date existed. Forcing it to UTC would move
 * every pre-existing signature taken in a US evening on to the next day — a
 * regression dressed up as consistency. UTC belongs to the audit trail, where
 * the shared instant is the point; see {@link formatCeremonyTimestamp}.
 *
 * A declared date carries no time, because a calendar date has none and pinning
 * a clock to it would show a moment the signer never stated.
 *
 * Use this rather than reading `signed_at` directly. The one place that should
 * not is the audit trail, which exists to show when the ceremony really
 * happened — see SignatureHistoryDialog.
 */
export function formatSignatureDate(signature: SignatureDates): string {
  if (signature.signature_date) {
    // parseISOToLocalDate, not `new Date(str)`: a date-only string parses as
    // UTC midnight and renders as the previous day west of Greenwich.
    const declared = parseISOToLocalDate(signature.signature_date);
    if (declared) {
      return declared.toLocaleDateString('en-US', DATE_ONLY);
    }
  }
  const dt = new Date(signature.signed_at);
  if (Number.isNaN(dt.getTime())) {
    return '';
  }
  return dt.toLocaleString('en-US', { ...DATE_ONLY, ...TIME_OF_DAY });
}
